/**
 * UI-specific Zod schemas for parsing/validation
 *
 * NOTE: API response schemas are now in @aica/api-types/schemas (SSOT).
 * This file contains only UI-specific utilities that don't map to API contracts.
 *
 * @see @aica/api-types/schemas for all API response schemas
 * @see lib/api/validation.ts for validateApiResponse utility
 */

import { z } from 'zod/v4';
import { DEFAULT_LANGUAGE } from '@aica/core';
import { logError } from './logger';

/**
 * Settings JSON schema for project settings
 * Used to parse settings_json from API responses
 *
 * This is a UI-specific schema for parsing the JSON string stored in project.settingsJson.
 * It's not an API response schema - the API returns settingsJson as a raw JSON string.
 */
export const projectSettingsSchema = z
  .object({
    description: z.string().optional(),
    channels: z.array(z.string()).optional().default([]),
    asrProvider: z.string().optional().nullable(),
    llmProvider: z.string().optional().nullable(),
    // 'auto' | en | ru | uk | es — the language analysis reports are written
    // in. Declared explicitly (not left to .passthrough()) so mapper code can
    // read it typed, the same way asrProvider/llmProvider are.
    analysisLanguage: z.string().optional().nullable(),
    language: z.string().length(2).optional().default(DEFAULT_LANGUAGE),
    timezone: z.string().optional(),
    currency: z.string().length(3).optional(),
  })
  .passthrough(); // Allow additional fields

export type ProjectSettings = z.infer<typeof projectSettingsSchema>;

/**
 * Parse project settings from JSON string with validation
 * Returns default settings if parsing fails
 */
export function parseProjectSettings(
  settingsJson: string | null | undefined
): ProjectSettings {
  if (!settingsJson || settingsJson === 'undefined' || settingsJson === 'null') {
    return projectSettingsSchema.parse({});
  }

  try {
    const parsed = JSON.parse(settingsJson);
    return projectSettingsSchema.parse(parsed);
  } catch (error) {
    logError(
      'Failed to parse project settings',
      { settingsJson },
      error instanceof Error ? error : undefined
    );
    // Return safe defaults instead of throwing
    return projectSettingsSchema.parse({});
  }
}
